import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PlayCircle } from "lucide-react";

/**
 * VideoDemoModal — opens when "Watch demo" is clicked.
 * Currently shows a placeholder (record-your-screen prompt). When you
 * upload the MP4, set videoSrc (e.g. https://cdn.../slotbook-demo.mp4)
 * and the player auto-replaces the placeholder.
 */
export default function VideoDemoModal({ open, onClose, videoSrc, posterSrc }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-xl px-4"
          onClick={onClose}
          data-testid="video-demo-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
            style={{
              background:
                "radial-gradient(at 20% 30%, rgba(167,139,250,0.18), transparent 50%), #0A0A12",
              boxShadow:
                "0 40px 100px -20px rgba(167,139,250,0.4), 0 24px 80px -24px rgba(0,0,0,0.7)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close video"
              className="absolute right-4 top-4 z-10 inline-flex size-9 items-center justify-center rounded-full bg-white/10 text-white/90 backdrop-blur transition-colors hover:bg-white/20"
              data-testid="close-video-demo"
            >
              <X className="size-5" />
            </button>

            {videoSrc ? (
              <video
                src={videoSrc}
                poster={posterSrc}
                controls
                autoPlay
                playsInline
                className="aspect-video w-full bg-black"
                data-testid="video-demo-player"
              />
            ) : (
              <div className="flex aspect-video w-full flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="flex size-20 items-center justify-center rounded-full bg-violet-500/15 ring-1 ring-violet-300/30">
                  <PlayCircle className="size-9 text-violet-200" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white sm:text-3xl">
                  Video demo coming soon
                </h3>
                <p className="max-w-md text-sm leading-relaxed text-slate-300">
                  Drop your screen-recording MP4 into{" "}
                  <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-violet-200">
                    /public/demo.mp4
                  </code>{" "}
                  and pass <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs text-violet-200">videoSrc</code> to this modal — it'll auto-play here.
                </p>
                <p className="mt-2 text-xs text-slate-500 font-mono uppercase tracking-widest">
                  Until then · book a live walkthrough below
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
